import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpDown, ChevronDown, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { api } from "../../lib/api";
import { formatAZN } from "../../lib/format";
import { useI18n } from "../../lib/i18n";
import { modalSurfaceEffect } from "../../lib/modalSurface";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

const COPY = {
  az: {
    region: "Məhsul filtrləri və sıralama",
    products: "məhsul",
    loading: "Məhsullar yenilənir",
    filters: "Filterlər",
    allFilters: "Bütün filterlər",
    filterTitle: "Filterlər",
    filterDescription: "Axtarışı ölçü, material, rəng və xidmətlərə görə dəqiqləşdirin.",
    sort: "Sırala",
    sortTitle: "Sıralama",
    sortDescription: "Məhsulların göstərilmə ardıcıllığını seçin.",
    clearAll: "Hamısını təmizlə",
    clearDraft: "Təmizlə",
    apply: "Filterləri tətbiq et",
    checking: "Nəticələr yoxlanılır…",
    show: "Göstər",
    activeFilters: "Aktiv filterlər",
    remove: "Sil",
    selected: "aktiv",
  },
  en: {
    region: "Product filters and sorting",
    products: "products",
    loading: "Updating pieces",
    filters: "Filters",
    allFilters: "All filters",
    filterTitle: "Filters",
    filterDescription: "Refine the edit by size, material, colour and atelier services.",
    sort: "Sort",
    sortTitle: "Sort pieces",
    sortDescription: "Choose how the collection is ordered.",
    clearAll: "Clear all",
    clearDraft: "Clear",
    apply: "Apply filters",
    checking: "Checking results…",
    show: "Show",
    activeFilters: "Active filters",
    remove: "Remove",
    selected: "active",
  },
};

const SORTS = [
  { value: "newest", az: "Ən yenilər", en: "Newest" },
  { value: "price_asc", az: "Qiymət: aşağıdan yuxarı", en: "Price: low to high" },
  { value: "price_desc", az: "Qiymət: yuxarıdan aşağı", en: "Price: high to low" },
  { value: "featured", az: "Seçilmişlər", en: "Featured" },
  { value: "bestselling", az: "Çox satılanlar", en: "Bestselling" },
  { value: "recommended", az: "Tövsiyə edilənlər", en: "Recommended" },
];

const FILTERS = [
  {
    // Options are loaded from /api/products/facets and scoped to the section
    // being browsed, so /outlet offers only the categories that hold something
    // past-season. The group disappears when the catalogue has nothing to say
    // — and on a category page, where the choice is already made.
    key: "category",
    az: "Kateqoriya",
    en: "Category",
    options: [],
  },
  {
    key: "size",
    az: "Ölçü",
    en: "Size",
    options: ["EU 46", "EU 48", "EU 50", "EU 52", "EU 54", "EU 56"].map((value) => ({ value, az: value, en: value })),
  },
  {
    key: "fit",
    az: "Kəsim",
    en: "Fit",
    options: [
      // "Tailored" sat untranslated between "Klassik" and "Rahat".
      { value: "tailored", az: "Dar kəsim", en: "Tailored" },
      { value: "regular", az: "Klassik", en: "Regular" },
      { value: "relaxed", az: "Rahat", en: "Relaxed" },
    ],
  },
  {
    key: "material",
    az: "Material",
    en: "Material",
    options: [
      { value: "wool", az: "Yun", en: "Wool" },
      { value: "linen", az: "Kətan", en: "Linen" },
      { value: "cotton", az: "Pambıq", en: "Cotton" },
      { value: "silk", az: "İpək", en: "Silk" },
      { value: "cashmere", az: "Kaşmir", en: "Cashmere" },
      { value: "leather", az: "Dəri", en: "Leather" },
    ],
  },
  {
    // The options here are a fallback only. `occasion` is matched against the
    // tag catalogue (Tag.normalized_name / Tag.slug), and this hardcoded list
    // drifted away from it: every one of these five values returned zero
    // products, while the real slugs — smart-casual (135 pieces), office-style
    // (60), wedding-edit (31) — were never offered. Underscore vs hyphen alone
    // hid 135 garments. Real options are loaded from /api/tags at runtime.
    key: "occasion",
    az: "Təyinat",
    en: "Occasion",
    options: [
      { value: "smart-casual", az: "Smart casual", en: "Smart casual" },
      { value: "office-style", az: "Ofis üslubu", en: "Office style" },
      { value: "wedding-edit", az: "Toy seçimi", en: "Wedding edit" },
    ],
  },
  {
    key: "color",
    az: "Rəng",
    en: "Colour",
    options: [
      { value: "graphite", az: "Qrafit", en: "Graphite" },
      { value: "midnight_navy", az: "Gecə mavisi", en: "Midnight navy" },
      { value: "ivory", az: "Fil sümüyü", en: "Ivory" },
      { value: "deep_burgundy", az: "Tünd bordo", en: "Deep burgundy" },
      { value: "dark_green", az: "Tünd yaşıl", en: "Dark green" },
      { value: "champagne", az: "Şampan", en: "Champagne" },
    ],
  },
  {
    key: "availability",
    az: "Mövcudluq",
    en: "Availability",
    options: [
      { value: "in_stock", az: "Stokda", en: "In stock" },
      { value: "reserve", az: "Rezerv mümkündür", en: "Available to reserve" },
      { value: "made_to_order", az: "Sifarişlə hazırlanır", en: "Made to order" },
    ],
  },
];

const FEATURES = [
  { key: "new_arrivals", az: "Yeni", en: "New" },
  { key: "bestseller", az: "Bestseller", en: "Bestseller" },
  { key: "ai_try_on", az: "AI ilə yoxla", en: "AI Try-on", icon: Sparkles },
];

const PRICE_RANGES = [
  { value: "", min: "", max: "", az: "Bütün qiymətlər", en: "All prices" },
  { value: "0:100000", min: "", max: "100000", az: "1 000 ₼-dək", en: "Under 1,000 ₼" },
  { value: "100000:175000", min: "100000", max: "175000", az: "1 000–1 750 ₼", en: "1,000–1,750 ₼" },
  { value: "175000:225000", min: "175000", max: "225000", az: "1 750–2 250 ₼", en: "1,750–2,250 ₼" },
  { value: "225000:", min: "225000", max: "", az: "2 250 ₼ və yuxarı", en: "2,250 ₼ and above" },
];

const FILTER_PARAM_KEYS = [
  // FILTERS already carries "category"; the listing reads it as ?category=<handle>
  // and widens it to the whole subtree server-side.
  ...FILTERS.map((filter) => filter.key),
  ...FEATURES.map((feature) => feature.key),
  "price_min",
  "price_max",
];

/** Replace the occasion group's options with the tags that actually have
 *  published products behind them. Anything else would offer a shopper a
 *  filter chip that can only ever return an empty grid. */
function resolveFilters(tagOptions, colourFacets, categoryFacets, categoryPinned) {
  return FILTERS.flatMap((filter) => {
    if (filter.key === "category") {
      // A chip that returns an empty grid is worse than no chip: drop the group
      // rather than offer a category this section does not stock.
      if (categoryPinned || !categoryFacets?.length) return [];
      return [{
        ...filter,
        options: categoryFacets.map((row) => ({
          value: row.value,
          az: row.title?.az || row.value,
          en: row.title?.en || row.title?.az || row.value,
        })),
      }];
    }
    if (filter.key === "occasion" && tagOptions?.length) {
      return [{ ...filter, options: tagOptions }];
    }
    // The colours the catalogue actually stocks, with the swatch that lets the
    // filter show the colour instead of naming it. Capped because the shop
    // carries 74 of them and a wall of every one is not a filter.
    if (filter.key === "color" && colourFacets?.length) {
      return [{
        ...filter,
        swatches: colourFacets.slice(0, 24),
      }];
    }
    return [filter];
  });
}

/* Tapşırıq (2026-10-05): sol paneldə ən yuxarıda KATEQORİYALAR, sonra Ölçü,
   Kəsim, Rəng, Qiymət; qalan qruplar onlardan sonra gəlir. */
const PRIMARY_FILTER_KEYS = ["category", "size", "fit", "color"];
const isPrimaryFilter = (filter) => PRIMARY_FILTER_KEYS.includes(filter.key);

function sameSet(a = [], b = []) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((value) => set.has(value));
}

function localized(item, locale) {
  return item?.[locale] || item?.az || item?.en || "";
}

function readFilterState(searchParams) {
  return FILTER_PARAM_KEYS.reduce((state, key) => ({ ...state, [key]: searchParams.get(key) || "" }), {});
}

function priceRangeValue(state) {
  const min = state.price_min || "";
  const max = state.price_max || "";
  return PRICE_RANGES.find((range) => range.min === min && range.max === max)?.value || "";
}

function stateToQuery(state) {
  return FILTER_PARAM_KEYS.reduce((query, key) => {
    if (state[key]) query[key] = state[key];
    return query;
  }, {});
}

export function getProductFilterQuery(searchParams) {
  return stateToQuery(readFilterState(searchParams));
}

export function clearProductFilters(searchParams, setSearchParams, options = { replace: true }) {
  const next = new URLSearchParams(searchParams);
  FILTER_PARAM_KEYS.forEach((key) => next.delete(key));
  next.delete("page");
  setSearchParams(next, options);
}

export default function ProductFilters({
  searchParams,
  setSearchParams,
  total = 0,
  loading = false,
  scopeParams = {},
  /* Kateqoriya səhifəsinin alt kateqoriyaları — checkbox, çox seçim:
     { options: [{ value, label }], selected: [handle], root, onChange(list, search) }.
     Seçim səhifənin URL-ini dəyişir (bir seçim → /categories/<alt>, bir neçə →
     /categories/<kök>?category=a,b), ona görə naviqasiyanı səhifə özü edir. */
  categoryGroup = null,
}) {
  const { locale } = useI18n();
  const copy = COPY[locale] || COPY.en;
  const sort = searchParams.get("sort") || "newest";
  const applied = useMemo(() => readFilterState(searchParams), [searchParams]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [draft, setDraft] = useState(applied);
  const appliedCategories = categoryGroup?.selected || [];
  const [draftCategories, setDraftCategories] = useState(appliedCategories);
  const [previewTotal, setPreviewTotal] = useState(total);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/tags")
      .then((response) => {
        if (!active) return;
        const rows = (response.data || [])
          .filter((tag) => Number(tag.product_count || 0) > 0)
          .sort((a, b) => Number(b.product_count || 0) - Number(a.product_count || 0))
          .map((tag) => ({ value: tag.slug, az: tag.name, en: tag.name }));
        setTagOptions(rows);
      })
      .catch(() => { /* keep the fallback options */ });
    return () => { active = false; };
  }, []);

  // Both sheets cover the whole phone screen, so the tab bar stands down while
  // either is open — otherwise it paints over the sheet's own commit button.
  useEffect(() => modalSurfaceEffect(filterOpen || sortOpen), [filterOpen, sortOpen]);

  // The colour list and the price bounds are the catalogue's, not a constant.
  // Six hardcoded colour slugs used to ship here — graphite, midnight_navy,
  // ivory and three more — and every one of them returned zero products,
  // because this catalogue names its colours in Azerbaijani. The top price
  // band had drifted the same way: it started above the dearest piece in the
  // shop, so it could never match anything either.
  const [facets, setFacets] = useState(null);
  useEffect(() => {
    let active = true;
    // Scoped, so /outlet is described by what /outlet actually holds.
    const params = {};
    if (scopeParams.outlet) params.outlet = true;
    if (scopeParams.on_sale) params.on_sale = true;
    api.get("/products/facets", { params })
      .then((response) => { if (active) setFacets(response.data || null); })
      .catch(() => { if (active) setFacets(null); });
    return () => { active = false; };
  }, [scopeParams.outlet, scopeParams.on_sale]);

  const filters = useMemo(
    () => resolveFilters(tagOptions, facets?.colours, facets?.categories, Boolean(scopeParams.category)),
    [tagOptions, facets, scopeParams.category],
  );
  const activeFilters = useMemo(() => buildActiveFilters(applied, locale, filters), [applied, locale, filters]);
  const activeCount = activeFilters.length;

  useEffect(() => {
    if (!filterOpen) return undefined;
    const controller = new AbortController();
    setPreviewLoading(true);
    setPreviewFailed(false);
    const timer = window.setTimeout(() => {
      const draftQuery = stateToQuery(draft);
      if (categoryGroup) delete draftQuery.category;
      api.get("/products", {
        params: {
          ...scopeParams,
          ...(categoryGroup ? { category: draftCategories.length ? draftCategories.join(",") : categoryGroup.root } : {}),
          ...draftQuery,
          page: 1,
          page_size: 1,
        },
        signal: controller.signal,
      })
        .then((response) => setPreviewTotal(Number(response.data?.total ?? 0)))
        .catch((error) => {
          if (error?.code !== "ERR_CANCELED" && error?.name !== "CanceledError") setPreviewFailed(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setPreviewLoading(false);
        });
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, draftCategories, filterOpen, scopeParams]);

  const commitValues = (values, options = { replace: true }) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(values).forEach(([key, value]) => {
      if (value && !(key === "sort" && value === "newest")) next.set(key, value);
      else next.delete(key);
    });
    next.delete("page");
    setSearchParams(next, options);
  };

  const openFilters = () => {
    setDraft(applied);
    setDraftCategories(appliedCategories);
    setPreviewTotal(total);
    setPreviewLoading(false);
    setPreviewFailed(false);
    setFilterOpen(true);
  };

  const applyDraft = (event) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    FILTER_PARAM_KEYS.forEach((key) => {
      if (categoryGroup && key === "category") return;
      if (draft[key]) next.set(key, draft[key]); else next.delete(key);
    });
    next.delete("page");
    setFilterOpen(false);
    if (categoryGroup && !sameSet(draftCategories, appliedCategories)) {
      categoryGroup.onChange(draftCategories, next);
      return;
    }
    setSearchParams(next);
  };

  const setDraftValue = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const setDraftPrice = (value) => {
    const range = PRICE_RANGES.find((item) => item.value === value) || PRICE_RANGES[0];
    setDraft((current) => ({ ...current, price_min: range.min, price_max: range.max }));
  };
  const clearDraft = () => {
    setDraft(FILTER_PARAM_KEYS.reduce((state, key) => ({ ...state, [key]: "" }), {}));
    setDraftCategories([]);
  };
  const toggleCategory = (list, value) => (
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value]
  );
  const categoryTitle = locale === "az" ? "Kateqoriyalar" : "Categories";
  const clearApplied = () => clearProductFilters(searchParams, setSearchParams);

  return (
    <section className="adf-plp-filter-shell" aria-label={copy.region} data-testid="plp-controls">
      <aside className="adf-plp-desktop-filter-panel" data-testid="plp-desktop-filters">
        <div className="adf-plp-sidebar-head">
          <h2>{copy.filters}</h2>
          <span>{loading ? copy.loading : `${total} ${copy.products}`}</span>
        </div>

        {categoryGroup?.options?.length > 0 && (
          <details className="adf-plp-sidebar-group adf-plp-sidebar-group--categories" open data-testid="plp-desktop-categories">
            <summary>
              <span>{categoryTitle}</span>
              <ChevronDown size={15} aria-hidden="true" />
            </summary>
            <div className="adf-plp-sidebar-options">
              {categoryGroup.options.map((option) => {
                const selected = appliedCategories.includes(option.value);
                return (
                  <label key={option.value} className={`adf-plp-filter-option ${selected ? "is-selected" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => categoryGroup.onChange(toggleCategory(appliedCategories, option.value))}
                      data-testid={`plp-desktop-category-${option.value}`}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </details>
        )}

        {filters.filter(isPrimaryFilter).map((filter, index) => (
          <DesktopFilterGroup
            key={filter.key}
            filter={filter}
            locale={locale}
            value={applied[filter.key]}
            onChange={(value) => commitValues({ [filter.key]: value })}
            open={(!categoryGroup?.options?.length && index < 1) || Boolean(applied[filter.key])}
          />
        ))}

        <details className="adf-plp-sidebar-group" open={Boolean(applied.price_min || applied.price_max)}>
          <summary>
            <span>{locale === "az" ? "Qiymət" : "Price"}</span>
            <ChevronDown size={15} aria-hidden="true" />
          </summary>
          <div className="adf-plp-sidebar-options">
            <PriceSlider
              bounds={facets?.price}
              locale={locale}
              min={applied.price_min}
              max={applied.price_max}
              onCommit={(next) => commitValues({ price_min: next.min, price_max: next.max })}
              testId="plp-price-slider"
            />
          </div>
        </details>

        {filters.filter((filter) => !isPrimaryFilter(filter)).map((filter) => (
          <DesktopFilterGroup
            key={filter.key}
            filter={filter}
            locale={locale}
            value={applied[filter.key]}
            onChange={(value) => commitValues({ [filter.key]: value })}
            open={Boolean(applied[filter.key])}
          />
        ))}

        <details className="adf-plp-sidebar-group">
          <summary>
            <span>{locale === "az" ? "Xüsusiyyətlər" : "Features"}</span>
            <ChevronDown size={15} aria-hidden="true" />
          </summary>
          <div className="adf-plp-sidebar-options">
            {FEATURES.map((feature) => {
              const selected = applied[feature.key] === "true";
              return (
                <label key={feature.key} className={`adf-plp-filter-option ${selected ? "is-selected" : ""}`}>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(event) => commitValues({ [feature.key]: event.target.checked ? "true" : "" })}
                    data-testid={`plp-desktop-feature-${feature.key}`}
                  />
                  <span>{localized(feature, locale)}</span>
                </label>
              );
            })}
          </div>
        </details>

        {activeCount > 0 && (
          <button type="button" className="adf-plp-sidebar-clear" onClick={clearApplied} data-testid="plp-desktop-clear-all">
            {copy.clearAll}
          </button>
        )}
      </aside>

      <div className="adf-plp-filter-toolbar" data-testid="plp-toolbar">
        <p className="adf-plp-result-count" role="status" aria-live="polite" aria-atomic="true" data-testid="plp-result-count">
          {loading ? copy.loading : `${total} ${copy.products}`}
        </p>

        <DesktopSort locale={locale} copy={copy} sort={sort} onChange={(value) => commitValues({ sort: value })} />

        <div className="adf-plp-mobile-controls">
          <FilterTrigger copy={copy} activeCount={activeCount} onClick={openFilters} expanded={filterOpen} testId="plp-filter-trigger-mobile" />
          <button
            type="button"
            className="adf-plp-mobile-sort"
            onClick={() => setSortOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={sortOpen}
            aria-controls="plp-sort-dialog"
            data-testid="plp-sort-trigger"
          >
            <ArrowUpDown size={16} aria-hidden="true" />
            <span>{copy.sort}</span>
            <span className="adf-plp-mobile-sort__value">{localized(SORTS.find((item) => item.value === sort), locale)}</span>
          </button>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="adf-plp-active-row">
          <ul className="adf-plp-active-filters" aria-label={copy.activeFilters} data-testid="plp-active-filters">
            {activeFilters.map((filter) => (
              <li key={filter.key}>
                <button
                  type="button"
                  onClick={() => commitValues(filter.clearValues)}
                  aria-label={`${copy.remove}: ${filter.label}`}
                  className="adf-plp-active-chip"
                  data-testid={`plp-filter-chip-${filter.key}`}
                >
                  {filter.label}
                  <X size={13} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="adf-plp-clear-all" onClick={clearApplied} data-testid="plp-clear-all">
            {copy.clearAll}
          </button>
        </div>
      )}

      <Sheet open={filterOpen} onOpenChange={(open) => {
        if (open) openFilters(); else setFilterOpen(false);
      }}>
        {/* Aşağıdan açılır (tapşırıq 2026-10-05, emporium mobil kimi); ilk blok
            kateqoriya səhifəsində KATEQORİYALAR-dır. */}
        <SheetContent id="plp-filter-drawer" side="bottom" className="adf-plp-filter-sheet p-0" data-testid="plp-filter-drawer">
          <form className="adf-plp-filter-form" onSubmit={applyDraft} data-testid="plp-filter-form">
            <SheetHeader className="adf-plp-filter-header">
              <SheetTitle className="font-serif text-[22px] font-normal leading-none text-[var(--adf-ink)]">{copy.filterTitle}</SheetTitle>
              {/* Təsvir cümləsi başlıqda 100px-dən çox yer tuturdu; ekran
                  oxucuları üçün qalır, göz üçün yox. */}
              <SheetDescription className="sr-only">{copy.filterDescription}</SheetDescription>
            </SheetHeader>

            <div className="adf-plp-filter-body">
              {categoryGroup?.options?.length > 0 && (
                <details className="adf-plp-msec" open data-testid="plp-filter-group-categories">
                  <summary>
                    <span className="adf-plp-msec__title">{categoryTitle}</span>
                    <span className={`adf-plp-msec__value ${draftCategories.length ? "is-set" : ""}`}>
                      {draftCategories.length
                        ? `${draftCategories.length} ${locale === "az" ? "seçilib" : "set"}`
                        : (locale === "az" ? "Hamısı" : "All")}
                    </span>
                    <ChevronDown size={15} aria-hidden="true" />
                  </summary>
                  <div className="adf-plp-msec__body">
                    <div className="adf-plp-filter-options adf-plp-filter-options--chips">
                      {categoryGroup.options.map((option) => (
                        <label key={option.value} className={`adf-plp-filter-option ${draftCategories.includes(option.value) ? "is-selected" : ""}`}>
                          <input
                            type="checkbox"
                            checked={draftCategories.includes(option.value)}
                            onChange={() => setDraftCategories((current) => toggleCategory(current, option.value))}
                            data-testid={`plp-filter-category-${option.value}`}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </details>
              )}

              {filters.filter(isPrimaryFilter).map((filter) => (
                <FilterGroup
                  key={filter.key}
                  filter={filter}
                  locale={locale}
                  value={draft[filter.key]}
                  onChange={(value) => setDraftValue(filter.key, value)}
                />
              ))}

              <details className="adf-plp-msec" open={Boolean(draft.price_min || draft.price_max)} data-testid="plp-filter-group-price">
                <summary>
                  <span className="adf-plp-msec__title">{locale === "az" ? "Qiymət" : "Price"}</span>
                  <span className={`adf-plp-msec__value ${draft.price_min || draft.price_max ? "is-set" : ""}`}>
                    {draft.price_min || draft.price_max
                      ? (locale === "az" ? "Seçilib" : "Set")
                      : (locale === "az" ? "Hamısı" : "All")}
                  </span>
                  <ChevronDown size={15} aria-hidden="true" />
                </summary>
                <div className="adf-plp-msec__body">
                  <PriceSlider
                    bounds={facets?.price}
                    locale={locale}
                    min={draft.price_min}
                    max={draft.price_max}
                    onCommit={(next) => setDraft((current) => ({ ...current, price_min: next.min, price_max: next.max }))}
                    testId="plp-price-slider-mobile"
                  />
                </div>
              </details>

              {filters.filter((filter) => !isPrimaryFilter(filter)).map((filter) => (
                <FilterGroup
                  key={filter.key}
                  filter={filter}
                  locale={locale}
                  value={draft[filter.key]}
                  onChange={(value) => setDraftValue(filter.key, value)}
                />
              ))}

              <details className="adf-plp-msec" open={FEATURES.some((feature) => draft[feature.key] === "true")} data-testid="plp-filter-group-features">
                <summary>
                  <span className="adf-plp-msec__title">{locale === "az" ? "Xüsusiyyətlər" : "Features"}</span>
                  <span className={`adf-plp-msec__value ${FEATURES.some((f) => draft[f.key] === "true") ? "is-set" : ""}`}>
                    {FEATURES.filter((f) => draft[f.key] === "true").length
                      ? `${FEATURES.filter((f) => draft[f.key] === "true").length} ${locale === "az" ? "seçilib" : "set"}`
                      : (locale === "az" ? "Hamısı" : "All")}
                  </span>
                  <ChevronDown size={15} aria-hidden="true" />
                </summary>
                <div className="adf-plp-msec__body">
                  <div className="adf-plp-filter-options adf-plp-filter-options--chips">
                    {FEATURES.map((feature) => (
                      <label key={feature.key} className={`adf-plp-filter-option ${draft[feature.key] === "true" ? "is-selected" : ""}`}>
                        <input
                          type="checkbox"
                          checked={draft[feature.key] === "true"}
                          onChange={(event) => setDraftValue(feature.key, event.target.checked ? "true" : "")}
                          data-testid={`plp-filter-option-${feature.key}`}
                        />
                        <span>{localized(feature, locale)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </details>
            </div>

            <div className="adf-plp-filter-footer">
              <button type="button" className="adf-plp-filter-clear" onClick={clearDraft} data-testid="plp-filter-clear-draft">
                {copy.clearDraft}
              </button>
              <button type="submit" className="adf-cta adf-cta-dark adf-plp-filter-apply" disabled={previewLoading} data-testid="plp-filter-apply">
                {previewLoading
                  ? copy.checking
                  : previewFailed
                    ? copy.apply
                    : `${copy.show} ${previewTotal} ${copy.products}`}
              </button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <SortSheet
        open={sortOpen}
        onOpenChange={setSortOpen}
        locale={locale}
        copy={copy}
        sort={sort}
        onChange={(value) => {
          commitValues({ sort: value });
          setSortOpen(false);
        }}
      />
    </section>
  );
}

/** Two range inputs sharing one track. Native inputs keep the keyboard and
 *  screen-reader behaviour that a div-and-pointer-events slider throws away. */
function PriceSlider({ bounds, locale, min, max, onCommit, testId }) {
  const step = 1000; // 10 AZN in minor units — finer than anyone drags for.
  // Rounded outward onto the step grid. A range input snaps to min + n·step, so
  // with raw bounds of 20952–221936 the upper handle stopped at 220952 and the
  // dearest piece in the shop sat outside every range the slider could express.
  const floor = Math.floor(Number(bounds?.min ?? 0) / step) * step;
  const ceiling = Math.ceil(Number(bounds?.max ?? 0) / step) * step;
  const ready = ceiling > floor;

  const [low, setLow] = useState(() => (min ? Number(min) : floor));
  const [high, setHigh] = useState(() => (max ? Number(max) : ceiling));

  // The bounds arrive after the first paint, and the URL can change under us.
  useEffect(() => {
    if (!ready) return;
    setLow(min ? Math.max(floor, Number(min)) : floor);
    setHigh(max ? Math.min(ceiling, Number(max)) : ceiling);
  }, [min, max, floor, ceiling, ready]);

  const commit = (nextLow, nextHigh) => {
    onCommit({
      min: nextLow > floor ? String(nextLow) : "",
      max: nextHigh < ceiling ? String(nextHigh) : "",
    });
  };

  if (!ready) return null;

  const pct = (value) => ((value - floor) / (ceiling - floor)) * 100;

  return (
    <div className="adf-plp-price" data-testid={testId}>
      <div className="adf-plp-price-readout">
        <span>{formatAZN(low, locale)}</span>
        <span>{formatAZN(high, locale)}</span>
      </div>
      <div className="adf-plp-price-track">
        <span className="adf-plp-price-fill" style={{ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }} aria-hidden="true" />
        <input
          type="range"
          min={floor}
          max={ceiling}
          step={step}
          value={low}
          aria-label={locale === "az" ? "Ən aşağı qiymət" : "Minimum price"}
          onChange={(event) => setLow(Math.min(Number(event.target.value), high - step))}
          onMouseUp={() => commit(low, high)}
          onTouchEnd={() => commit(low, high)}
          onKeyUp={() => commit(low, high)}
          data-testid={`${testId}-min`}
        />
        <input
          type="range"
          min={floor}
          max={ceiling}
          step={step}
          value={high}
          aria-label={locale === "az" ? "Ən yuxarı qiymət" : "Maximum price"}
          onChange={(event) => setHigh(Math.max(Number(event.target.value), low + step))}
          onMouseUp={() => commit(low, high)}
          onTouchEnd={() => commit(low, high)}
          onKeyUp={() => commit(low, high)}
          data-testid={`${testId}-max`}
        />
      </div>
    </div>
  );
}

/** Colour is a thing you look at. Spelling it out — and in a vocabulary the
 *  catalogue does not use — was how the whole group came to lead nowhere. */
function ColourSwatches({ options, value, onChange, locale, testId }) {
  const selected = value || "";
  return (
    <div className="adf-plp-swatches" data-testid={testId}>
      {options.map((colour) => {
        const active = selected.toLowerCase() === colour.value.toLowerCase();
        const label = colour.count
          ? `${colour.value} (${colour.count})`
          : colour.value;
        return (
          <button
            key={colour.value}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            onClick={() => onChange(active ? "" : colour.value)}
            className={`adf-plp-swatch ${active ? "is-selected" : ""}`}
          >
            <span style={{ background: colour.swatch_hex || "#d8d5d1" }} aria-hidden="true" />
          </button>
        );
      })}
      {selected && (
        <button type="button" onClick={() => onChange("")} className="adf-plp-swatch-clear">
          {locale === "az" ? "Təmizlə" : "Clear"}
        </button>
      )}
    </div>
  );
}

function DesktopFilterGroup({ filter, locale, value, onChange, open }) {
  if (filter.swatches) {
    return (
      <details className="adf-plp-sidebar-group" open={open}>
        <summary>
          <span>{localized(filter, locale)}</span>
          <ChevronDown size={15} aria-hidden="true" />
        </summary>
        <div className="adf-plp-sidebar-options">
          <ColourSwatches
            options={filter.swatches}
            value={value}
            onChange={onChange}
            locale={locale}
            testId="plp-colour-swatches"
          />
        </div>
      </details>
    );
  }
  return <DesktopFilterGroupOptions filter={filter} locale={locale} value={value} onChange={onChange} open={open} />;
}

function DesktopFilterGroupOptions({ filter, locale, value, onChange, open }) {
  return (
    <details className="adf-plp-sidebar-group" open={open}>
      <summary>
        <span>{localized(filter, locale)}</span>
        <ChevronDown size={15} aria-hidden="true" />
      </summary>
      <div className="adf-plp-sidebar-options">
        <OptionRadio
          group={`${filter.key}-desktop`}
          value=""
          label={locale === "az" ? "Hamısı" : "All"}
          checked={!value}
          onChange={onChange}
        />
        {filter.options.map((option) => (
          <OptionRadio
            key={option.value}
            group={`${filter.key}-desktop`}
            value={option.value}
            label={localized(option, locale)}
            checked={value === option.value}
            onChange={onChange}
          />
        ))}
      </div>
    </details>
  );
}

function FilterTrigger({ copy, activeCount, onClick, expanded, testId }) {
  const label = activeCount ? `${copy.filters}, ${activeCount} ${copy.selected}` : copy.filters;
  return (
    <button
      type="button"
      className="adf-plp-filter-trigger"
      onClick={onClick}
      aria-label={label}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      aria-controls="plp-filter-drawer"
      data-testid={testId}
    >
      <SlidersHorizontal size={16} aria-hidden="true" />
      <span>{copy.allFilters}</span>
      {activeCount > 0 && <span className="adf-plp-filter-count" data-testid="plp-filter-count">{activeCount}</span>}
    </button>
  );
}

function DesktopSort({ locale, copy, sort, onChange }) {
  return (
    <label className="adf-plp-desktop-sort">
      <span>{copy.sort}:</span>
      <select value={sort} onChange={(event) => onChange(event.target.value)} aria-label={copy.sort} data-testid="plp-sort">
        {SORTS.map((item) => <option key={item.value} value={item.value}>{localized(item, locale)}</option>)}
      </select>
      <ChevronDown size={14} aria-hidden="true" />
    </label>
  );
}

/**
 * Mobil bölmə: yığılan sətir + çip seçimlər. Əvvəl hər qrup nəhəng 2 sütunlu
 * qutu-düymələrlə tam açıq gəlirdi — təkcə kateqoriya bir ekrandan çox yer
 * tuturdu. İndi hər bölmə bir 54px sətirdir (başlıq + cari seçim), yalnız
 * toxunulan bölmə açılır və seçimlər yan-yana düzülən kiçik çiplərdir.
 * Dəyəri olan bölmə açıq gəlir ki, aktiv seçim heç vaxt gizli qalmasın.
 */
function FilterGroup({ filter, locale, value, onChange }) {
  const all = locale === "az" ? "Hamısı" : "All";
  const selectedLabel = value
    ? (filter.swatches ? value : localized(filter.options.find((o) => o.value === value), locale) || value)
    : all;
  return (
    <details className="adf-plp-msec" open={Boolean(value)} data-testid={`plp-filter-group-${filter.key}`}>
      <summary>
        <span className="adf-plp-msec__title">{localized(filter, locale)}</span>
        <span className={`adf-plp-msec__value ${value ? "is-set" : ""}`}>{selectedLabel}</span>
        <ChevronDown size={15} aria-hidden="true" />
      </summary>
      <div className="adf-plp-msec__body">
        {filter.swatches ? (
          <ColourSwatches
            options={filter.swatches}
            value={value}
            onChange={onChange}
            locale={locale}
            testId="plp-colour-swatches-mobile"
          />
        ) : (
          <div className="adf-plp-filter-options adf-plp-filter-options--chips" role="radiogroup" aria-label={localized(filter, locale)}>
            <OptionRadio
              group={filter.key}
              value=""
              label={all}
              checked={!value}
              onChange={onChange}
            />
            {filter.options.map((option) => (
              <OptionRadio
                key={option.value}
                group={filter.key}
                value={option.value}
                label={localized(option, locale)}
                checked={value === option.value}
                onChange={onChange}
              />
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

function OptionRadio({ group, value, label, checked, onChange }) {
  const idValue = value || "all";
  return (
    <label className={`adf-plp-filter-option ${checked ? "is-selected" : ""}`}>
      <input
        type="radio"
        name={`plp-${group}`}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        data-testid={`plp-filter-option-${group}-${String(idValue).replace(/\s+/g, "-").toLowerCase()}`}
      />
      <span>{label}</span>
    </label>
  );
}

function SortSheet({ open, onOpenChange, locale, copy, sort, onChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent id="plp-sort-dialog" side="bottom" className="adf-plp-sort-sheet p-0" data-testid="plp-sort-dialog">
        <SheetHeader className="adf-plp-filter-header">
          <SheetTitle className="font-serif text-[30px] font-normal leading-none text-[var(--adf-ink)]">{copy.sortTitle}</SheetTitle>
          <SheetDescription className="text-[13px] text-[var(--adf-gray-1)]">{copy.sortDescription}</SheetDescription>
        </SheetHeader>
        <fieldset className="adf-plp-sort-options">
          <legend className="sr-only">{copy.sortTitle}</legend>
          {SORTS.map((item) => (
            <label key={item.value} className={`adf-plp-sort-option ${sort === item.value ? "is-selected" : ""}`}>
              <input
                type="radio"
                name="plp-sort"
                value={item.value}
                checked={sort === item.value}
                onChange={() => onChange(item.value)}
                data-testid={`plp-sort-option-${item.value}`}
              />
              <span>{localized(item, locale)}</span>
            </label>
          ))}
        </fieldset>
      </SheetContent>
    </Sheet>
  );
}

function buildActiveFilters(state, locale, filters = FILTERS) {
  const rows = [];
  filters.forEach((filter) => {
    const value = state[filter.key];
    if (!value) return;
    const option = filter.options.find((item) => item.value === value);
    rows.push({ key: filter.key, label: localized(option, locale) || value, clearValues: { [filter.key]: "" } });
  });
  FEATURES.forEach((feature) => {
    if (state[feature.key] === "true") {
      rows.push({ key: feature.key, label: localized(feature, locale), clearValues: { [feature.key]: "" } });
    }
  });
  if (state.price_min || state.price_max) {
    const range = PRICE_RANGES.find((item) => item.value === priceRangeValue(state));
    rows.push({
      key: "price",
      label: localized(range, locale) || (locale === "az" ? "Qiymət aralığı" : "Price range"),
      clearValues: { price_min: "", price_max: "" },
    });
  }
  return rows;
}
